'use client'

/**
 * Prefetch a route on the visitor's intent instead of on sight.
 *
 * A <Link> that sits in the first viewport of every page prefetches its route
 * the moment the page loads, and the App Router's prefetch preloads that
 * route's client chunks too. The chrome's brand mark links home from every
 * page, so every non-home page loaded the homepage's JS (~70 KB compressed)
 * during load (perf budget, 2026-11-04: community-hub and housing-market over
 * their jsTransferBytes ceilings). Pass `prefetch={false}` on the Link and
 * spread these handlers on it: the route still prefetches before the click,
 * on hover, keyboard focus or the start of a tap, and never on page load.
 *
 * Each handler prefetches at most once per mount. A router without `prefetch`
 * (a test double) is a no-op, never a throw: a missed prefetch only means the
 * click fetches the route itself.
 */
import { useCallback, useRef } from 'react'
import { useRouter } from 'next/navigation'

export type IntentPrefetchHandlers = {
  onPointerEnter: () => void
  onFocus: () => void
  onTouchStart: () => void
}

export function useIntentPrefetch(href: string): IntentPrefetchHandlers {
  const router = useRouter()
  const done = useRef<string | null>(null)
  const prefetch = useCallback(() => {
    if (done.current === href) return
    if (typeof router?.prefetch !== 'function') return
    done.current = href
    try {
      router.prefetch(href)
    } catch {
      done.current = null
    }
  }, [router, href])
  return { onPointerEnter: prefetch, onFocus: prefetch, onTouchStart: prefetch }
}
